import React, {useEffect, useState} from "react";
import {
    Col,
    Form,
    Row,
    Create,
    useModal,
    useForm,
} from "@pankod/refine-antd";
import {IResourceComponentsProps} from "@pankod/refine-core";
import {IProductBundle, Product} from "Interfaces";
import {ProductModal} from "./Components/ProductModal";
import {CodeFormItem, NameFormItem, StatusFormItem, SlugFormItem, DescriptionFormItem} from "./Components/FormItems";
import {CoverImageSection} from "./Components/CoverImageSection";
import {MemberProductsTable} from "./Components/MemberProductsTable";
import {useBundleImage} from "./Components/useBundleImage";
import {withGeneratedBundleSlug} from "./Components/slug";

export const ProductBundleCreate: React.FC<IResourceComponentsProps> = () => {
    const {show: showProductModal, close: closeProductModal, modalProps} = useModal();
    const {formProps, saveButtonProps, form} = useForm<IProductBundle>({
        resource: "product-bundles",
        action: "create",
    });

    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

    const img = useBundleImage({products: selectedProducts, form});

    useEffect(() => {
        form.setFieldsValue({products: selectedProducts.map(product => product.id)});
    }, [selectedProducts, form]);

    // Default status to draft for new bundles
    useEffect(() => {
        form.setFieldsValue({status: "draft"});
    }, [form]);

    const handleQuantityChange = (sku: string, value: number) => {
        if (value < 1) return;
        const newQuantities = {...quantities, [sku]: value};
        setQuantities(newQuantities);
        form.setFieldsValue({quantityTable: JSON.stringify(newQuantities)});
    };

    return (
        <Create saveButtonProps={saveButtonProps}>
            <ProductModal
                modalProps={modalProps}
                closeProductModal={closeProductModal}
                setSelectedIds={setSelectedProducts}
                initialSelectedIds={[]}
            />

            <Form {...formProps} onFinish={withGeneratedBundleSlug(formProps.onFinish)} layout="vertical">
                <Row gutter={{xs: 8, sm: 16, md: 24, lg: 32}}>
                    <Col><NameFormItem/></Col>
                    <Col><CodeFormItem/></Col>
                    <Col><StatusFormItem/></Col>
                    <Col><SlugFormItem/></Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <DescriptionFormItem/>
                    </Col>
                    <Col span={12}>
                        <CoverImageSection
                            coverImageUrl={img.coverImageUrl}
                            uploading={img.uploading}
                            generating={img.generating}
                            aiPrompt={img.aiPrompt}
                            setAiPrompt={img.setAiPrompt}
                            showAiPrompt={img.showAiPrompt}
                            setShowAiPrompt={img.setShowAiPrompt}
                            buildDefaultPrompt={img.buildDefaultPrompt}
                            handleUpload={img.handleUpload}
                            handleGenerateImage={img.handleGenerateImage}
                            canGenerate={!!selectedProducts?.length}
                        />
                        <Form.Item name="coverImage" hidden>
                            <input type="hidden"/>
                        </Form.Item>
                    </Col>
                </Row>

                <MemberProductsTable
                    selectedProducts={selectedProducts}
                    quantities={quantities}
                    handleQuantityChange={handleQuantityChange}
                    onRemove={(record) => setSelectedProducts(selectedProducts.filter(p => p.id !== record.id))}
                    onAssign={showProductModal}
                />

                <Form.Item name="products" hidden>
                    <input type="hidden"/>
                </Form.Item>
                <Form.Item name="quantityTable" hidden>
                    <input type="hidden"/>
                </Form.Item>
            </Form>
        </Create>
    );
};
